import { useCallback, useEffect, useRef, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type { JobsState, ServiceRunStatus, ServiceRunsPage } from '../api/monitor';

/**
 * Mais curto que o das outras telas (60 s): aqui a pergunta é "o que está
 * rodando agora", e uma rodada de checagem de saúde inteira dura menos que um
 * minuto — com a cadência das outras telas ela nasceria e morreria entre dois
 * refreshes, sem nunca aparecer como em execução.
 */
const REFRESH_MS = 20_000;

export interface JobsController {
  state: JobsState | null;
  runs: ServiceRunsPage | null;
  /** Slug do job filtrado no histórico; nulo = todos. */
  jobFilter: string | null;
  statusFilter: ServiceRunStatus | null;
  loading: boolean;
  /** Erro do carregamento dos jobs (a tela inteira falhou). */
  error: string | null;
  /** Erro do histórico; a lista de jobs continua utilizável sem ele. */
  runsError: string | null;
  /** Slug do job que está sendo disparado pela tela. */
  triggering: string | null;
  setJobFilter: (job: string | null) => void;
  setStatusFilter: (status: ServiceRunStatus | null) => void;
  goToPage: (page: number) => void;
  reload: () => Promise<void>;
  runNow: (job: string) => Promise<void>;
}

export function useJobs(): JobsController {
  const [state, setState] = useState<JobsState | null>(null);
  const [runs, setRuns] = useState<ServiceRunsPage | null>(null);
  const [jobFilter, setJob] = useState<string | null>(null);
  const [statusFilter, setStatus] = useState<ServiceRunStatus | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);

  // O refresh automático e a troca de filtro competem pela mesma lista; só a
  // última página pedida pode escrever na tela.
  const latestRequest = useRef(0);

  const loadJobs = useCallback(async () => {
    try {
      setState(await monitorApi.jobs());
      setError(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    try {
      const response = await monitorApi.jobRuns({ job: jobFilter, status: statusFilter, page });
      if (latestRequest.current !== requestId) return;
      setRuns(response);
      setRunsError(null);
    } catch (cause) {
      if (latestRequest.current !== requestId) return;
      setRunsError((cause as Error).message);
    }
  }, [jobFilter, statusFilter, page]);

  const reload = useCallback(async () => {
    await Promise.all([loadJobs(), loadRuns()]);
  }, [loadJobs, loadRuns]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  // Com a aba escondida não adianta gastar request.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [reload]);

  const setJobFilter = useCallback((job: string | null) => {
    setJob(job);
    setPage(1);
  }, []);

  const setStatusFilter = useCallback((status: ServiceRunStatus | null) => {
    setStatus(status);
    setPage(1);
  }, []);

  const goToPage = useCallback((next: number) => setPage(Math.max(1, next)), []);

  /**
   * O disparo devolve os jobs já atualizados, mas o histórico não vem junto —
   * a rodada nova só aparece na lista depois do `loadRuns`.
   */
  const runNow = useCallback(
    async (job: string) => {
      setTriggering(job);
      try {
        setState(await monitorApi.runJob(job));
        setError(null);
      } finally {
        setTriggering(null);
        void loadRuns();
      }
    },
    [loadRuns]
  );

  return {
    state,
    runs,
    jobFilter,
    statusFilter,
    loading,
    error,
    runsError,
    triggering,
    setJobFilter,
    setStatusFilter,
    goToPage,
    reload,
    runNow,
  };
}
